import { Injectable } from '@angular/core';
import { Microservices } from '@microservices';
import { User } from 'interfaces/common/db/models/auth';
import { AuthService } from './services/back-services/auth.service';
import { Subscription, distinctUntilChanged } from 'rxjs';
import { CacheListener } from './@utils/cache-listener';
import { Repository } from './@core/classes/Repository.class';
import { DbService } from './@core/db.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  users: Repository<User>;
  user: CacheListener<User | null> = new CacheListener("user", null, this.syncUser.bind(this));
  employees: CacheListener<User[]> = new CacheListener("employees", null, this.syncEmployees.bind(this));
  drivers: CacheListener<User[]> = new CacheListener("drivers", null, this.syncDrivers.bind(this));
  listeners: Subscription[] = [];

  constructor(
    private micro: Microservices,
    private authService: AuthService,
    private db: DbService
  ) {
    this.users = new Repository<User>({
      sync: this._getUser.bind(this),
      db: this.db.users
    });
  }

  setupListeners () {
    this.listeners.push(
      //* When the token changes, sync the user
      this.authService.token
        .pipe(distinctUntilChanged((prev, cur) => prev === cur))
        .subscribe((token) => this.syncUser()),

      //* When the token is cleared, clear the user
      this.user.bind(this.authService.token),
      this.employees.bind(this.user),
      this.drivers.bind(this.user)
    );
  }

  removeListeners () {
    this.listeners.forEach(listener => listener.unsubscribe());
  }

  private async _getUser (id: string) {
    const token = await this.authService.token.get();
    
    return await this.micro.DexAuth.get(id, token);
  }

  async getUser (id: string) {
    return await this.users.value(id);
  }

  async userListener (id: string) {
    return await this.users.listener(id);
  }

  async syncEmployees () {
    const user = await this.user.get();
    if (!user || user._accessLevel !== 'admin') return [];
    
    const token = await this.authService.token.get();
    const employees = await this.micro.DexAuth.findMyEmployees({ limit: '', skip: 0, token }, token);

    return employees.results.reverse() as User[];
  }

  async syncDrivers () {
    const user = await this.user.get();
    if (!user || user.role !== 'carrier') return [];
    
    const token = await this.authService.token.get();
    const drivers = await this.micro.DexAuth.myDrivers(token);

    return drivers.reverse() as User[];
  }

  async syncUser (_user?: User) {
    const token = await this.authService.token.get();
    const user = _user ?? await this.micro.DexAuth.user(token);

    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('userId', user._id);
    localStorage.setItem('role', user.role);
    if (user.companyId) {
      localStorage.setItem('companyId', user.companyId);
    } else {
      localStorage.removeItem('companyId');
    }

    return user;
  }

  async editDriver (driverId: string, driver: any) {
    const token = await this.authService.token.get();

    await this.micro.DexAuth.editDriver(driverId, driver, token);

    this.drivers.sync();
  }

  async fetchUser (id: string) {
    const token = await this.authService.token.get();

    return await this.micro.getUser(id, token, true);
  }

  async createDriver (driverInfo: any, shipmentId?: string, validation?: string) {
    const token = await this.authService.token.get();

    const driver = await this.micro.DexAuth.newDriver(driverInfo, token, shipmentId, validation);

    this.drivers.sync();

    return driver;
  }

  clear () {
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    localStorage.removeItem('role');
    localStorage.removeItem('companyId');

    this.user.clear();
  }

}
